import { useCallback, useEffect, useMemo, useState } from "react";
import { findFirstComponentOfType, TextExtractor } from "../internal/TextExtractor";
import { useFileContext } from "../../state/FileContext";
import { useFavoritesContext } from "../../state/FavoritesContext";
import { useSiteContext } from "../../state/SiteContext";
import { useFilterContext } from "../../state/FilterContext";
import { getFavoritesKey, getFavorites, setFavorites } from "../../state/favorites-storage";
import { SourceItem } from "../../shared/models/source-item.model";

interface AddFavoriteProps {
  label?: string;
  children: React.ReactNode;
}

/**
 * Button that adds the current file (from state) to favorites.
 * Disabled when the file is already in favorites.
 * Label from `label` prop or arg-label child (TextExtractor).
 */
export function AddFavorite({ label: labelProp, children }: AddFavoriteProps): React.ReactElement {
  const { fileState } = useFileContext();
  const { siteState } = useSiteContext();
  const { filterState } = useFilterContext();
  const { favoritesVersion, invalidateFavorites } = useFavoritesContext();

  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState("");

  const [disabled, setDisabled] = useState(true);
  useEffect(() => {
    const path = fileState.path;
    const siteName = siteState.site?.name;
    if (!path || !siteName) {
      setDisabled(true);
      return;
    }
    const key = getFavoritesKey(siteName);
    const favorites = getFavorites(key);
    const alreadyInFavorites = favorites.some((item) => item.value === path);
    setDisabled(alreadyInFavorites);
  }, [fileState.path, siteState.site?.name, favoritesVersion]);

  const currentItem = useMemo((): SourceItem | null => {
    const path = fileState.path;
    const meta = fileState.metadata;
    if (!path || !meta) return null;
    const title = meta.title ?? "";
    const validity = filterState.validity ?? "";
    const system: Record<string, string[]> = {
      title: [title],
      validity: [validity],
    };
    if (meta.lang != null && meta.lang !== "") {
      system.lang = [meta.lang];
    }
    return {
      value: path,
      props: { system },
    };
  }, [fileState.path, fileState.metadata, filterState.validity]);

  const clickHandler = useCallback(() => {
    const path = fileState.path;
    const siteName = siteState.site?.name;
    if (!path || !siteName || !currentItem) return;
    const key = getFavoritesKey(siteName);
    const favorites = getFavorites(key);
    if (favorites.some((item) => item.value === path)) return;
    setFavorites(key, [...favorites, currentItem]);
    invalidateFavorites();
    setDisabled(true);
  }, [fileState.path, siteState.site?.name, currentItem, invalidateFavorites]);

  const displayLabel = labelProp ?? labelValue;
  const button = useMemo(() => {
    if (disabled) {
      return <button disabled={true}>{displayLabel}</button>;
    }
    return <button onClick={clickHandler}>{displayLabel}</button>;
  }, [disabled, displayLabel, clickHandler]);

  return (
    <>
      {labelChild != null && (
        <TextExtractor onExtractedText={setLabelValue}>{labelChild.props.children}</TextExtractor>
      )}
      {button}
    </>
  );
}
